import type { SearchSchemaInput } from '@tanstack/react-router';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { RecipeList } from '@/components/recipes/recipe-list';
import { RECIPE_SORTS, type RecipeSort } from '@/lib/recipe-search';
import { useReturnTicket } from '@/lib/return-ticket';
import {
  parseSearchEnum,
  parseSearchFlag,
  parseSearchList,
  parseSearchString,
} from '@/lib/search-params';

/**
 * What `/recipes` reads out of its own URL.
 *
 * A brewer who searches "pils", opens a recipe to check it is not the one
 * they want, and presses Back should land on the search they just typed —
 * not an empty list. Putting the query, the tag chips, the sort and the
 * archive toggle in the URL is what makes Back (and a copied link) restore
 * that view instead of losing it, the same way `id` does for `/ingredients`
 * (see that route for the `undefined`-not-`null` reasoning this follows).
 */
export interface RecipesSearch {
  /** The search box. */
  readonly q: string | undefined;
  /** Active tag chips, from a repeated `?tag=` key. */
  readonly tag: readonly string[] | undefined;
  /** The sort control, or `undefined` for the default (`updated`). */
  readonly sort: RecipeSort | undefined;
  /**
   * The "Archived" chip. `archived`, not `showArchived`: the key is read
   * back out of an address bar, not out of a props object, and `?archived`
   * names the state being looked at rather than the act of a component
   * showing it. `true | undefined` rather than `boolean`, because `false`
   * (the default) is never worth a character in the URL — see
   * `parseSearchFlag`.
   */
  readonly archived: true | undefined;
}

export const Route = createFileRoute('/recipes/')({
  validateSearch: (
    search: {
      readonly q?: unknown;
      readonly tag?: unknown;
      readonly sort?: unknown;
      readonly archived?: unknown;
    } & SearchSchemaInput,
  ): RecipesSearch => ({
    q: parseSearchString(search.q),
    tag: parseSearchList(search.tag),
    sort: parseSearchEnum(search.sort, RECIPE_SORTS),
    archived: parseSearchFlag(search.archived),
  }),
  component: RecipesPage,
});

function RecipesPage() {
  const { q, tag, sort, archived } = Route.useSearch();
  // Scoped `from`, not the bare hook: an unscoped `useNavigate` types `search`
  // against every route's schema at once (registered app-wide), which cannot
  // know this route's own `q`/`tag`/`sort` shape and rejects the updater below.
  const navigate = useNavigate({ from: Route.fullPath });
  // The view is in the URL; this is what lets something come back to it. Every
  // way out of the list stamps the entry it pushes with this list's own
  // position, so the sub page's back link returns to the search the brewer was
  // looking at rather than pushing a bare `/recipes` over the top of it
  // (PLAN-LIST-STATE D-LS7).
  const returnTicket = useReturnTicket();
  return (
    <RecipeList
      // The search *is* the view: `RecipeList` reads these on every render
      // rather than seeding from them, so a Back that only changes the search
      // params — which does not remount this route — moves the list with it.
      query={q}
      tagFilter={tag}
      sort={sort}
      showArchived={archived}
      // `replace: true`: a keystroke narrows the same search, it does not
      // start a new one, so Back should undo the whole search box rather than
      // walk back through "p", "pi", "pil" one letter at a time. An empty box
      // writes `undefined` so a cleared search leaves `?q=` out of the URL
      // entirely rather than round-tripping to `?q=` (see `search-params.ts`).
      onQueryChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, q: next === '' ? undefined : next }),
          replace: true,
        });
      }}
      // Choosing a tag is a decision, not a keystroke: it pushes, so Back
      // undoes one chip at a time instead of jumping past the search that led
      // to it.
      onTagFilterChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, tag: next.length === 0 ? undefined : next }),
        });
      }}
      onSortChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, sort: next === 'updated' ? undefined : next }),
        });
      }}
      // A filter choice, not a keystroke: it pushes, same as a tag or a sort.
      onShowArchivedChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, archived: next ? true : undefined }),
        });
      }}
      // "Clear" empties the search box and the chips together, so it is one
      // navigation and one history entry. Two — a replace for the box then a
      // push for the chips — would leave Back on a half-cleared list nobody
      // was ever shown.
      onClearFilters={() => {
        void navigate({
          search: (prev) => ({ ...prev, q: undefined, tag: undefined }),
        });
      }}
      onOpen={(recipeId) => {
        void navigate({ to: '/recipes/$recipeId', params: { recipeId }, state: returnTicket });
      }}
      // Creating is a *place*, not a press (see `routes/recipes/new.tsx`):
      // this list writes nothing and goes somewhere, so the recipe is made
      // with the list already unmounted rather than appearing on it for a
      // frame first. The ticket rides along exactly as it does for `onOpen`,
      // and `/recipes/new` hands it on to the recipe it replaces itself with,
      // so "My Recipes" still returns to this search.
      onCreate={() => {
        void navigate({ to: '/recipes/new', state: returnTicket });
      }}
      onTrySample={() => {
        void navigate({ to: '/recipes/new', search: { sample: true }, state: returnTicket });
      }}
      // The two halves of one dialog's answer, exactly as `/recipes/$recipeId`
      // threads them to the editor (PLAN-UX-PASS D1): a brew day opens on the
      // checklist, a plan opens on the batch page, and both carry the list's
      // own ticket so the way back is the search the brewer left.
      onBrewDay={(batchId) => {
        void navigate({
          to: '/batches/$batchId/brew',
          params: { batchId },
          state: returnTicket,
        });
      }}
      onPlanned={(batchId) => {
        void navigate({ to: '/batches/$batchId', params: { batchId }, state: returnTicket });
      }}
    />
  );
}
